import React, { useState } from 'react';
import api from '../config/axiosConfig';
import { showErrorMsg } from '../config/common';
import { API_URLS, MAXPREPS_STAT_SUPPLIER_ID } from '../config/Constants';
import { buildMaxPrepsLacrosseExport } from '../utils/maxPrepsExport';
import Modal, { ModalActions, ModalContent, ModalTitle } from './basic/Modal';

interface ComprehensiveExportProps {
  possessionPlayers: any[];
  offensePlayers: any[];
  goaliePlayers: any[];
  faceoffPlayers: any[];
  penaltyPlayers: any[];
  blockedShotsPlayers: any[];
  possessionColumns: any[];
  offenseColumns: any[];
  goalieColumns: any[];
  faceoffColumns: any[];
  penaltyColumns: any[];
  blockedShotsColumns: any[];
  teamName?: string;
  seasonName?: string;
  className?: string;
  clientId: string | null;
  sortConfigs?: {
    possession: { key: string; direction: 'asc' | 'desc' } | null;
    offense: { key: string; direction: 'asc' | 'desc' } | null;
    goalie: { key: string; direction: 'asc' | 'desc' } | null;
    faceoff: { key: string; direction: 'asc' | 'desc' } | null;
    penalty: { key: string; direction: 'asc' | 'desc' } | null;
    blockedShots: { key: string; direction: 'asc' | 'desc' } | null;
  };
}

export const ComprehensiveExportButton: React.FC<ComprehensiveExportProps> = ({
  possessionPlayers,
  offensePlayers,
  goaliePlayers,
  faceoffPlayers,
  penaltyPlayers,
  blockedShotsPlayers,
  possessionColumns,
  offenseColumns,
  goalieColumns,
  faceoffColumns,
  penaltyColumns,
  blockedShotsColumns,
  teamName = 'Team',
  seasonName = 'Season',
  className = '',
  clientId = null,
  sortConfigs,
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailForm, setEmailForm] = useState({
    to: '',
    subject: `${teamName} - Comprehensive Statistics Report`,
    message: `Please find attached the comprehensive statistics report for ${teamName} including all player performance metrics.`,
  });
  const [emailStatus, setEmailStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });
  const [infoModal, setInfoModal] = useState<string | null>(null);

  const exportErrorMessage = (error: any, fallback: string) => {
    const raw = error?.response?.data?.error || error?.message || fallback;
    if (error?.code === 'ECONNABORTED' || /timeout/i.test(String(raw))) {
      return 'The export timed out. Please try again.';
    }
    return typeof raw === 'string' ? raw : fallback;
  };

  const downloadBlob = (content: string, filename: string, type: string) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Convert column definitions to simple format for backend
  const convertColumns = (columns: any[]) => {
    // List of calculated special columns that should be included
    const calculatedSpecialColumns = ['PPG', 'GPG', 'GAPG'];

    return columns
      .filter(
        (col) =>
          !col.header.toLowerCase().includes('position') &&
          !col.header.toLowerCase().includes('#') &&
          !col.header.toLowerCase().includes('player name') &&
          !col.header.toLowerCase().includes('player') &&
          col.header !== 'GP' && // Filter out GP since we add "Games Played" separately
          col.header !== 'G+/-' && // Filter out Goals +/- (special column)
          // For special columns, only include those in our calculated list
          (col.key !== 'special' || calculatedSpecialColumns.includes(col.header)),
      )
      .map((col) => {
        // Handle special columns (PPG, GPG, GAPG)
        if (col.key === 'special') {
          return {
            header: col.header,
            key: col.header, // Use header as key for special columns
            dataKey: col.header,
          };
        }
        return {
          header: col.header,
          key: String(col.key),
          dataKey: String(col.key),
        };
      });
  };

  const addPlayerInfo = (columns: any[]) => [
    { header: '#', key: 'playerNumber', dataKey: 'playerNumber' },
    { header: 'Player Name', key: 'playerName', dataKey: 'playerName' },
    { header: 'Games Played', key: 'gamesPlayed', dataKey: 'gamesPlayed' },
    ...columns,
  ];

  const handleComprehensiveExport = async () => {
    setIsExporting(true);
    try {
      const reports = [];

      // Only include tables that have data
      if (possessionPlayers.length > 0) {
        reports.push({
          title: 'Possession Statistics',
          data: possessionPlayers,
          columns: addPlayerInfo(convertColumns(possessionColumns)),
          sortConfig: sortConfigs?.possession,
        });
      }

      if (offensePlayers.length > 0) {
        // Add calculated fields (PPG, GPG) to offense data
        const offenseDataWithCalculated = offensePlayers.map((player) => ({
          ...player,
          PPG: player.gamesPlayed > 0 ? parseFloat((player.points / player.gamesPlayed).toFixed(1)) : 0,
          GPG: player.gamesPlayed > 0 ? parseFloat((player.goals / player.gamesPlayed).toFixed(1)) : 0,
        }));

        reports.push({
          title: 'Offensive Statistics',
          data: offenseDataWithCalculated,
          columns: addPlayerInfo(convertColumns(offenseColumns)),
          sortConfig: sortConfigs?.offense,
        });
      }

      if (goaliePlayers.length > 0) {
        // Add calculated fields (GAPG) to goalie data
        const goalieDataWithCalculated = goaliePlayers.map((player) => ({
          ...player,
          GAPG: player.gamesPlayed > 0 ? parseFloat((player.goalsAllowed / player.gamesPlayed).toFixed(1)) : 0,
        }));

        reports.push({
          title: 'Goalie Statistics',
          data: goalieDataWithCalculated,
          columns: addPlayerInfo(convertColumns(goalieColumns)),
          sortConfig: sortConfigs?.goalie,
        });
      }

      if (faceoffPlayers.length > 0) {
        reports.push({
          title: 'Face-off Statistics',
          data: faceoffPlayers,
          columns: addPlayerInfo(convertColumns(faceoffColumns)),
          sortConfig: sortConfigs?.faceoff,
        });
      }

      if (penaltyPlayers.length > 0) {
        reports.push({
          title: 'Penalty Statistics',
          data: penaltyPlayers,
          columns: addPlayerInfo(convertColumns(penaltyColumns)),
          sortConfig: sortConfigs?.penalty,
        });
      }

      if (blockedShotsPlayers.length > 0) {
        reports.push({
          title: 'Blocked Shots Statistics',
          data: blockedShotsPlayers,
          columns: addPlayerInfo(convertColumns(blockedShotsColumns)),
          sortConfig: sortConfigs?.blockedShots,
        });
      }

      if (reports.length === 0) {
        setInfoModal('No data available to export');
        return;
      }

      // Send data to backend for PDF generation
      const response = await api.post(
        API_URLS.EXPORT_DOWNLOAD_COMPREHENSIVE,
        {
          reports,
          teamName,
          seasonName,
          clientId,
        },
        {
          responseType: 'blob',
          timeout: 60000,
        },
      );

      if (response.status === 200) {
        // Handle PDF download
        const blob = response.data;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${teamName}-Comprehensive-Report-${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        throw new Error(response.data?.error || 'Failed to generate PDF');
      }
    } catch (error: any) {
      console.error('Error generating comprehensive PDF:', error);
      showErrorMsg(exportErrorMessage(error, 'Error generating PDF. Please try again.'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleMaxPrepsExport = () => {
    const result = buildMaxPrepsLacrosseExport({
      supplierId: MAXPREPS_STAT_SUPPLIER_ID,
      possessionPlayers,
      offensePlayers,
      goaliePlayers,
      faceoffPlayers,
      penaltyPlayers,
    });

    if (result.playerCount === 0) {
      setInfoModal('No player statistics available to export');
      return;
    }

    const safeTeamName = teamName.replace(/[^a-z0-9-]+/gi, '-').replace(/^-+|-+$/g, '') || 'Team';
    downloadBlob(
      result.content,
      `${safeTeamName}-MaxPreps-Lacrosse-${new Date().toISOString().split('T')[0]}.txt`,
      'text/plain;charset=utf-8',
    );
  };

  const handleEmailComprehensive = async () => {
    if (!emailForm.to.trim()) {
      setEmailStatus({
        type: 'error',
        message: 'Please enter an email address',
      });
      return;
    }

    setEmailStatus({ type: null, message: '' });
    setIsExporting(true);

    try {
      const reports = [];

      // Only include tables that have data
      if (possessionPlayers.length > 0) {
        reports.push({
          title: 'Possession Statistics',
          data: possessionPlayers,
          columns: addPlayerInfo(convertColumns(possessionColumns)),
          sortConfig: sortConfigs?.possession,
        });
      }

      if (offensePlayers.length > 0) {
        // Add calculated fields (PPG, GPG) to offense data
        const offenseDataWithCalculated = offensePlayers.map((player) => ({
          ...player,
          PPG: player.gamesPlayed > 0 ? parseFloat((player.points / player.gamesPlayed).toFixed(1)) : 0,
          GPG: player.gamesPlayed > 0 ? parseFloat((player.goals / player.gamesPlayed).toFixed(1)) : 0,
        }));

        reports.push({
          title: 'Offensive Statistics',
          data: offenseDataWithCalculated,
          columns: addPlayerInfo(convertColumns(offenseColumns)),
          sortConfig: sortConfigs?.offense,
        });
      }

      if (goaliePlayers.length > 0) {
        // Add calculated fields (GAPG) to goalie data
        const goalieDataWithCalculated = goaliePlayers.map((player) => ({
          ...player,
          GAPG: player.gamesPlayed > 0 ? parseFloat((player.goalsAllowed / player.gamesPlayed).toFixed(1)) : 0,
        }));

        reports.push({
          title: 'Goalie Statistics',
          data: goalieDataWithCalculated,
          columns: addPlayerInfo(convertColumns(goalieColumns)),
          sortConfig: sortConfigs?.goalie,
        });
      }

      if (faceoffPlayers.length > 0) {
        reports.push({
          title: 'Face-off Statistics',
          data: faceoffPlayers,
          columns: addPlayerInfo(convertColumns(faceoffColumns)),
          sortConfig: sortConfigs?.faceoff,
        });
      }

      if (penaltyPlayers.length > 0) {
        reports.push({
          title: 'Penalty Statistics',
          data: penaltyPlayers,
          columns: addPlayerInfo(convertColumns(penaltyColumns)),
          sortConfig: sortConfigs?.penalty,
        });
      }

      if (blockedShotsPlayers.length > 0) {
        reports.push({
          title: 'Blocked Shots Statistics',
          data: blockedShotsPlayers,
          columns: addPlayerInfo(convertColumns(blockedShotsColumns)),
          sortConfig: sortConfigs?.blockedShots,
        });
      }

      if (reports.length === 0) {
        setEmailStatus({
          type: 'error',
          message: 'No data available to export',
        });
        return;
      }

      // Send data to backend for PDF generation and email
      const response = await api.post(
        API_URLS.EXPORT_EMAIL_COMPREHENSIVE,
        {
          to: emailForm.to,
          subject: emailForm.subject,
          message: emailForm.message,
          reports,
          teamName,
          seasonName,
          clientId,
        },
        { timeout: 60000 },
      );

      if (response.data.success) {
        setEmailStatus({
          type: 'success',
          message: 'Email sent successfully!',
        });
        setShowEmailForm(false);
      } else {
        throw new Error(response.data.message || 'Failed to send email');
      }
    } catch (error: any) {
      console.error('Error sending comprehensive email:', error);
      setEmailStatus({
        type: 'error',
        message: error.response?.data?.message || error.message || 'Error sending email. Please try again.',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <div className={`flex flex-wrap sm:flex-nowrap gap-3 w-full ${className}`}>
        <button
          onClick={handleComprehensiveExport}
          disabled={isExporting}
          className="flex flex-1 min-w-[calc(50%-0.375rem)] sm:min-w-0 sm:w-full text-nowrap items-center justify-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isExporting ? (
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="7,10 12,15 17,10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          )}
          {isExporting ? 'Generating PDF...' : 'Download PDF'}
        </button>

        <button
          onClick={() => setShowEmailForm(true)}
          disabled={isExporting}
          className="flex flex-1 min-w-[calc(50%-0.375rem)] sm:min-w-0 sm:w-full items-center justify-center gap-2 bg-green-600 text-white px-4 py-3 rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
          Email PDF
        </button>

        <button
          onClick={handleMaxPrepsExport}
          disabled={isExporting}
          className="flex w-full sm:flex-1 sm:w-full items-center justify-center gap-2 bg-[#05396b] text-white px-4 py-3 rounded-lg font-semibold hover:bg-[#04305a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
            <polyline points="14,2 14,8 20,8" />
            <line x1="8" y1="13" x2="16" y2="13" />
            <line x1="8" y1="17" x2="16" y2="17" />
            <polyline points="10,9 9,9 8,9" />
          </svg>
          Download MaxPreps
        </button>
      </div>

      <Modal isOpen={!!infoModal} onClose={() => setInfoModal(null)} maxWidth="sm" aria-labelledby="export-info-title">
        <ModalTitle id="export-info-title">Export</ModalTitle>
        <ModalContent>
          <p className="text-gray-700">{infoModal}</p>
        </ModalContent>
        <ModalActions>
          <button
            type="button"
            onClick={() => setInfoModal(null)}
            className="bg-[#05396b] text-white px-4 py-2 rounded-md hover:bg-[#04305a] transition-colors"
          >
            OK
          </button>
        </ModalActions>
      </Modal>

      {/* Email Form Modal */}
      {showEmailForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Email Comprehensive Report</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To Email Address</label>
                <input
                  type="email"
                  value={emailForm.to}
                  onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="recipient@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  value={emailForm.message}
                  onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {emailStatus.message && (
                <div
                  className={`p-3 rounded-md text-sm ${
                    emailStatus.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}
                >
                  {emailStatus.message}
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowEmailForm(false);
                  setEmailStatus({ type: null, message: '' });
                }}
                disabled={isExporting}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>

              <button
                onClick={handleEmailComprehensive}
                disabled={isExporting}
                className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isExporting ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
